import React from 'react';
import { TrendingUp, TrendingDown, Minus, AlertCircle } from 'lucide-react';

/**
 * BracketingDashboard Component
 * Visual dashboard for bracketing analysis
 *
 * Colors: GREEN (2+ both above AND below) | YELLOW (partial bracket)
 */
const BracketingDashboard = ({ bracketingData }) => {
  if (!bracketingData || !bracketingData.features) {
    return null;
  }

  const { features, overallBracketQuality } = bracketingData;

  const featureConfig = [
    { key: 'gla', icon: '📐', label: 'GLA (Living Area)' },
    { key: 'lotSize', icon: '📏', label: 'Lot Size' },
    { key: 'age', icon: '📅', label: 'Age (Year Built)' },
    { key: 'bedrooms', icon: '🛏️', label: 'Bedrooms' },
    { key: 'bathrooms', icon: '🚿', label: 'Bathrooms' },
    { key: 'garage', icon: '🚗', label: 'Garage Spaces' }
  ];

  const getColorStyle = (color) => {
    if (color === 'GREEN') {
      return {
        bg: 'bg-green-900/30',
        border: 'border-green-500',
        text: 'text-green-400',
        badge: 'bg-green-600'
      };
    } else {
      return {
        bg: 'bg-yellow-900/30',
        border: 'border-yellow-500',
        text: 'text-yellow-400',
        badge: 'bg-yellow-600'
      };
    }
  };

  const getQualityStyle = (rating) => {
    if (rating === 'Excellent' || rating === 'Good') {
      return 'text-green-400 bg-green-900/30 border-green-500';
    } else if (rating === 'Fair') {
      return 'text-yellow-400 bg-yellow-900/30 border-yellow-500';
    } else {
      return 'text-orange-400 bg-orange-900/30 border-orange-500';
    }
  };

  return (
    <div className="space-y-4">
      {/* Overall Quality Card */}
      <div className={`p-4 rounded-lg border ${getQualityStyle(overallBracketQuality.rating)}`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold mb-1">Overall Bracket Quality</h3>
            <p className="text-sm opacity-80">
              {overallBracketQuality.greenFeatures} of {overallBracketQuality.totalFeatures} features well bracketed
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{overallBracketQuality.rating}</div>
            <div className="text-sm opacity-80">{overallBracketQuality.percentage}%</div>
          </div>
        </div>
      </div>

      {/* Feature Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {featureConfig.map((config) => {
          const feature = features[config.key];
          if (!feature) return null;

          const style = getColorStyle(feature.color);

          return (
            <div
              key={config.key}
              className={`${style.bg} border ${style.border} rounded-lg p-4 transition-all hover:shadow-lg`}
            >
              {/* Feature Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{config.icon}</span>
                  <div>
                    <h4 className="font-semibold text-sm">{config.label}</h4>
                    <span className={`text-xs ${style.text} font-medium`}>
                      {feature.color === 'GREEN' ? '✓ Well Bracketed' : '⚠ Partial Bracket'}
                    </span>
                  </div>
                </div>
                <span className={`${style.badge} text-white text-xs px-2 py-1 rounded font-bold`}>
                  {feature.color}
                </span>
              </div>

              {/* Subject Value */}
              <div className="mb-3 pb-3 border-b border-gray-700">
                <div className="text-xs text-gray-400 mb-1">Subject Property</div>
                <div className="font-semibold text-white">
                  {formatFeatureValue(feature.subjectValue, config.key)}
                </div>
              </div>

              {/* Bracket Breakdown */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-400" />
                    <span className="text-gray-300">Above</span>
                  </div>
                  <span className={`font-semibold ${feature.above.length >= 2 ? 'text-green-400' : 'text-yellow-400'}`}>
                    {feature.above.length}
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Minus className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-300">Equal</span>
                  </div>
                  <span className="font-semibold text-gray-400">
                    {feature.equal.length}
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-purple-400" />
                    <span className="text-gray-300">Below</span>
                  </div>
                  <span className={`font-semibold ${feature.below.length >= 2 ? 'text-green-400' : 'text-yellow-400'}`}>
                    {feature.below.length}
                  </span>
                </div>
              </div>

              {/* Reason */}
              <div className="mt-3 pt-3 border-t border-gray-700">
                <p className="text-xs text-gray-400">{feature.reason}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info Box */}
      <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-300">
            <p className="font-semibold text-blue-400 mb-1">Understanding Bracketing</p>
            <p>
              <span className="text-green-400 font-semibold">GREEN</span> features have at least 2 comparables both above
              and below the subject property. <span className="text-yellow-400 font-semibold">YELLOW</span> features
              have limited bracketing.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function
const formatFeatureValue = (value, featureKey) => {
  if (value === null || value === undefined) return 'N/A';

  if (featureKey === 'gla' || featureKey === 'lotSize') {
    return `${Math.round(value).toLocaleString()} sq ft`;
  } else if (featureKey === 'age') {
    const age = new Date().getFullYear() - value;
    return `${value} (${age} years old)`;
  } else {
    return value.toString();
  }
};

export default BracketingDashboard;
